(() => {
  'use strict';

  class IgagyuCarousel extends HTMLElement {
    connectedCallback() {
      this.carouselStage = this.querySelector('.carousel-stage');
      this.slides = [...this.querySelectorAll('.cut-slide')];
      this.cutTabs = [...this.querySelectorAll('.cut-tabs button')];
      this.cutCounter = this.querySelector('#cut-counter');
      this.cutTitle = this.querySelector('#cut-title');
      this.cutDescription = this.querySelector('#cut-description');

      this.activeIndex = 0;
      this.pointerStart = null;
      this.didSwipe = false;
      this.autoTimer = null;

      this.bindEvents();
      this.renderSlider();
      this.startAutoAdvance();
    }

    disconnectedCallback() {
      if (this.autoTimer) {
        window.clearInterval(this.autoTimer);
      }
    }

    get slideCount() {
      return this.slides.length;
    }

    padNumber(value) {
      return String(value).padStart(2, '0');
    }

    positionFor(index) {
      const length = this.slideCount;
      if (length === 0) return 'active';

      let difference = (index - this.activeIndex + length) % length;
      if (difference > Math.floor(length / 2)) difference -= length;
      if (difference === 0) return 'active';
      if (difference === 1) return 'next';
      if (difference === -1) return 'previous';
      if (difference === 2) return 'far-next';
      return 'far-previous';
    }

    renderSlider() {
      if (this.slideCount === 0) return;

      this.slides.forEach((slide, index) => {
        const position = this.positionFor(index);
        slide.className = `cut-slide is-${position}`;
        slide.toggleAttribute('aria-current', position === 'active');
      });

      if (this.cutTabs.length) {
        this.cutTabs.forEach((button, index) => {
          const selected = index === this.activeIndex;
          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-pressed', String(selected));
        });
      }

      const activeSlide = this.slides[this.activeIndex];
      const name = activeSlide.dataset.name || '';
      const roman = activeSlide.dataset.roman || '';
      const description = activeSlide.dataset.description || '';

      if (this.cutCounter) {
        this.cutCounter.textContent = `${this.padNumber(this.activeIndex + 1)} / ${roman}`;
      }
      if (this.cutTitle) {
        this.cutTitle.textContent = name;
      }
      if (this.cutDescription) {
        this.cutDescription.textContent = description;
      }
    }

    setActive(index) {
      if (this.slideCount === 0) return;
      this.activeIndex = (index + this.slideCount) % this.slideCount;
      this.renderSlider();
    }

    goTo(direction) {
      this.setActive(this.activeIndex + direction);
    }

    bindEvents() {
      this.querySelectorAll('.stage-arrow').forEach((button) => {
        button.addEventListener('click', () => this.goTo(Number(button.dataset.direction)));
      });

      this.slides.forEach((slide) => {
        slide.addEventListener('click', () => {
          if (this.didSwipe) {
            this.didSwipe = false;
            return;
          }
          this.setActive(Number(slide.dataset.index));
        });
      });

      if (this.cutTabs.length) {
        this.cutTabs.forEach((button) => {
          button.addEventListener('click', () => this.setActive(Number(button.dataset.index)));
        });
      }

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.goTo(-1);
        if (event.key === 'ArrowRight') this.goTo(1);
      });

      this.addEventListener('pointerdown', (event) => {
        if (event.target.closest('.stage-arrow, .cut-tabs, a')) {
          this.pointerStart = null;
          this.didSwipe = false;
          return;
        }
        this.pointerStart = event.clientX;
        this.didSwipe = false;
      });

      this.addEventListener('pointermove', (event) => {
        if (this.pointerStart === null || Math.abs(event.clientX - this.pointerStart) <= 12) return;
        this.didSwipe = true;
        if (!this.hasPointerCapture(event.pointerId)) {
          this.setPointerCapture(event.pointerId);
        }
      });

      this.addEventListener('pointerup', (event) => {
        if (this.pointerStart === null) return;
        const distance = event.clientX - this.pointerStart;
        if (Math.abs(distance) > 48) {
          this.didSwipe = true;
          this.goTo(distance > 0 ? -1 : 1);
        }
        this.pointerStart = null;
      });

      this.addEventListener('pointercancel', () => {
        this.pointerStart = null;
      });
    }

    startAutoAdvance() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.autoTimer = window.setInterval(() => this.goTo(1), 6200);
    }
  }

  if (!customElements.get('igagyu-carousel')) {
    customElements.define('igagyu-carousel', IgagyuCarousel);
  }

  const moneyFormatter = new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency: 'JPY',
    maximumFractionDigits: 0,
  });

  function formatMoney(cents) {
    return moneyFormatter.format(cents / 100);
  }

  async function addItemsToCart(items) {
    const response = await fetch(`${window.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items }),
    });

    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Cart add failed');
    }
    return data;
  }

  async function refreshCartCount() {
    const countNodes = document.querySelectorAll('[data-igagyu-cart-count]');
    if (!countNodes.length) return;

    const response = await fetch(`${window.routes.cart_url}.js?t=${Date.now()}`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) return;

    const cart = await response.json();
    countNodes.forEach((node) => {
      node.textContent = cart.item_count;
    });
  }

  function bindSetAddButtons() {
    document.querySelectorAll('[data-igagyu-add-to-cart]').forEach((button) => {
      button.addEventListener('click', async () => {
        if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

        const variantId = Number(button.dataset.variantId);
        if (!variantId) return;

        const originalLabel = button.textContent;
        button.disabled = true;
        button.textContent = '追加中…';

        try {
          await addItemsToCart([{ id: variantId, quantity: 1 }]);
          await refreshCartCount();
          button.textContent = '追加しました';
          window.setTimeout(() => {
            button.textContent = originalLabel;
            button.disabled = false;
          }, 1800);
        } catch (error) {
          button.textContent = '追加に失敗しました';
          window.setTimeout(() => {
            button.textContent = originalLabel;
            button.disabled = false;
          }, 2200);
        }
      });
    });
  }

  class IgagyuSetBuilder extends HTMLElement {
    connectedCallback() {
      this.unitGrams = Number(this.dataset.unitGrams) || 100;
      this.minUnits = Number(this.dataset.minUnits) || 5;
      this.stepUnits = Number(this.dataset.stepUnits) || 5;
      this.minNoticeBelowTemplate =
        this.dataset.minNoticeBelow || '[grams]g以上でご注文いただけます';
      this.stepNoticeTemplate =
        this.dataset.stepNotice || '合計は[step]g単位（500g・1000g・1500g…）でご注文いただけます';
      this.setLabel = this.dataset.setLabel || 'IGANIKUオリジナル';
      this.items = [...this.querySelectorAll('[data-builder-item]')];
      this.totalWeightNode = this.querySelector('[data-total-weight]');
      this.totalPriceNode = this.querySelector('[data-total-price]');
      this.minNoticeNode = this.querySelector('[data-min-notice]');
      this.statusNode = this.querySelector('[data-builder-status]');
      this.submitButton = this.querySelector('[data-builder-submit]');
      this.breakdownWrap = this.querySelector('[data-builder-breakdown-wrap]');
      this.breakdownList = this.querySelector('[data-builder-breakdown]');
      this.quantities = new Map();

      this.items.forEach((item) => {
        this.quantities.set(item, 0);
      });

      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-action]');
        if (!button) return;

        const item = button.closest('[data-builder-item]');
        if (!item || item.dataset.available !== 'true') return;

        const action = button.dataset.action;
        const current = this.quantities.get(item) || 0;
        const next = action === 'increase' ? current + 1 : Math.max(0, current - 1);
        this.quantities.set(item, next);
        this.render();
      });

      if (this.submitButton) {
        this.submitButton.addEventListener('click', () => this.submit());
      }

      this.render();
    }

    get totals() {
      let units = 0;
      let price = 0;

      this.items.forEach((item) => {
        const qty = this.quantities.get(item) || 0;
        if (!qty) return;
        units += qty;
        price += qty * Number(item.dataset.price || 0);
      });

      return {
        units,
        grams: units * this.unitGrams,
        price,
      };
    }

    getSelectedEntries() {
      const entries = [];

      this.items.forEach((item) => {
        const qty = this.quantities.get(item) || 0;
        if (!qty) return;

        entries.push({
          name: item.dataset.name || '',
          qty,
          grams: qty * this.unitGrams,
          price: qty * Number(item.dataset.price || 0),
        });
      });

      return entries;
    }

    renderBreakdown() {
      if (!this.breakdownList) return;

      const entries = this.getSelectedEntries();
      this.breakdownList.replaceChildren();

      if (this.breakdownWrap) {
        this.breakdownWrap.hidden = entries.length === 0;
      }

      entries.forEach((entry) => {
        const row = document.createElement('li');
        row.className = 'builder-summary__breakdown-item';

        const leading = document.createElement('span');
        leading.className = 'builder-summary__breakdown-leading';
        leading.textContent = `${entry.name}×${entry.qty}`;

        const leader = document.createElement('span');
        leader.className = 'builder-summary__breakdown-leader';
        leader.setAttribute('aria-hidden', 'true');

        const sub = document.createElement('span');
        sub.className = 'builder-summary__breakdown-sub';
        sub.textContent = `${entry.grams}g / ${formatMoney(entry.price)}`;

        row.append(leading, leader, sub);
        this.breakdownList.append(row);
      });
    }

    formatNotice(template, grams, stepGrams) {
      return template
        .replace(/\[grams\]/g, String(grams))
        .replace(/\[step\]/g, String(stepGrams));
    }

    canSubmitSelection(units) {
      if (units === 0) return false;
      if (units < this.minUnits) return false;
      if (this.stepUnits > 0 && units % this.stepUnits !== 0) return false;
      return true;
    }

    render() {
      const { units, grams, price } = this.totals;
      const minGrams = this.minUnits * this.unitGrams;
      const stepGrams = this.stepUnits * this.unitGrams;
      const canSubmit = this.canSubmitSelection(units);

      this.items.forEach((item) => {
        const qty = this.quantities.get(item) || 0;
        const qtyNode = item.querySelector('[data-qty]');
        const decreaseButton = item.querySelector('[data-action="decrease"]');
        const increaseButton = item.querySelector('[data-action="increase"]');

        if (qtyNode) qtyNode.textContent = String(qty);
        if (decreaseButton) decreaseButton.disabled = qty === 0;
        if (increaseButton) {
          increaseButton.disabled = item.dataset.available !== 'true';
        }
      });

      if (this.totalWeightNode) this.totalWeightNode.textContent = String(grams);
      if (this.totalPriceNode) this.totalPriceNode.textContent = formatMoney(price);
      this.renderBreakdown();
      if (this.minNoticeNode) {
        if (canSubmit) {
          this.minNoticeNode.hidden = true;
        } else if (units === 0) {
          this.minNoticeNode.hidden = false;
          this.minNoticeNode.textContent = this.formatNotice(
            this.minNoticeBelowTemplate,
            minGrams,
            stepGrams
          );
        } else if (units < this.minUnits) {
          this.minNoticeNode.hidden = false;
          this.minNoticeNode.textContent = this.formatNotice(
            this.minNoticeBelowTemplate,
            minGrams,
            stepGrams
          );
        } else {
          this.minNoticeNode.hidden = false;
          this.minNoticeNode.textContent = this.formatNotice(
            this.stepNoticeTemplate,
            grams,
            stepGrams
          );
        }
      }
      if (this.submitButton) {
        this.submitButton.disabled = !canSubmit;
      }
    }

    async submit() {
      if (!this.submitButton || this.submitButton.disabled) return;

      const { units } = this.totals;
      if (!this.canSubmitSelection(units)) return;

      const payload = [];
      this.items.forEach((item) => {
        const qty = this.quantities.get(item) || 0;
        if (!qty) return;

        const variantId = Number(item.dataset.variantId);
        if (!variantId) return;

        payload.push({
          id: variantId,
          quantity: qty,
          properties: {
            セット: this.setLabel,
            表示名: item.dataset.name || '',
          },
        });
      });

      if (!payload.length) return;

      const originalLabel = this.submitButton.textContent;
      this.submitButton.disabled = true;
      this.submitButton.textContent = this.dataset.addingLabel || '追加しています…';
      if (this.statusNode) {
        this.statusNode.hidden = true;
        this.statusNode.textContent = '';
      }

      try {
        await addItemsToCart(payload);
        await refreshCartCount();

        this.quantities.forEach((_, item) => {
          this.quantities.set(item, 0);
        });
        this.render();

        if (this.statusNode) {
          this.statusNode.hidden = false;
          this.statusNode.textContent = this.dataset.addedLabel || 'カートに追加しました';
        }
        this.submitButton.textContent = originalLabel;
      } catch (error) {
        if (this.statusNode) {
          this.statusNode.hidden = false;
          this.statusNode.textContent = this.dataset.errorLabel || '追加に失敗しました';
        }
        this.submitButton.textContent = originalLabel;
        this.render();
      }
    }
  }

  if (!customElements.get('igagyu-set-builder')) {
    customElements.define('igagyu-set-builder', IgagyuSetBuilder);
  }

  function bindSmoothScroll() {
    const root = document.body;
    if (!root.classList.contains('igagyu-body')) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    root.addEventListener('click', (event) => {
      const link = event.target.closest('a[href^="#"]');
      if (!link) return;

      const hash = link.getAttribute('href');
      if (!hash || hash === '#') return;

      let target;
      try {
        const id = decodeURIComponent(hash.slice(1));
        target = document.getElementById(id);
      } catch {
        return;
      }
      if (!target) return;

      const linkUrl = new URL(link.href, window.location.href);
      if (linkUrl.pathname !== window.location.pathname) return;

      event.preventDefault();
      target.scrollIntoView({
        behavior: prefersReducedMotion.matches ? 'auto' : 'smooth',
        block: 'start',
      });

      if (window.history.pushState) {
        window.history.pushState(null, '', hash);
      } else {
        window.location.hash = hash;
      }
    });
  }

  function bindHeaderMenu() {
    const toggle = document.querySelector('[data-igagyu-menu-toggle]');
    const menu = document.getElementById('igagyu-site-menu');
    if (!toggle || !menu) return;

    const panel = menu.querySelector('.igagyu-site-menu__panel');
    const closeButtons = menu.querySelectorAll('[data-igagyu-menu-close]');
    const menuLinks = menu.querySelectorAll('[data-igagyu-menu-link]');
    const menuMotionMs = 380;
    let closeTimer = null;

    const finishClose = () => {
      if (menu.hidden) return;
      menu.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', 'メニューを開く');
      document.body.classList.remove('igagyu-menu-open');
    };

    const closeMenu = () => {
      if (menu.hidden) return;

      menu.classList.remove('is-open');
      if (closeTimer) window.clearTimeout(closeTimer);

      if (panel) {
        const onEnd = (event) => {
          if (event.target !== panel || event.propertyName !== 'transform') return;
          panel.removeEventListener('transitionend', onEnd);
          finishClose();
        };
        panel.addEventListener('transitionend', onEnd);
      }

      closeTimer = window.setTimeout(() => {
        if (!menu.classList.contains('is-open')) finishClose();
      }, menuMotionMs + 40);
    };

    const openMenu = () => {
      if (closeTimer) window.clearTimeout(closeTimer);
      menu.hidden = false;
      window.requestAnimationFrame(() => {
        menu.classList.add('is-open');
      });
      toggle.setAttribute('aria-expanded', 'true');
      toggle.setAttribute('aria-label', 'メニューを閉じる');
      document.body.classList.add('igagyu-menu-open');
      window.setTimeout(() => {
        menu.querySelector('.igagyu-site-menu__close')?.focus();
      }, menuMotionMs);
    };

    toggle.addEventListener('click', () => {
      if (menu.hidden || !menu.classList.contains('is-open')) {
        openMenu();
      } else {
        closeMenu();
        toggle.focus();
      }
    });

    closeButtons.forEach((button) => {
      button.addEventListener('click', closeMenu);
    });

    menuLinks.forEach((link) => {
      link.addEventListener('click', closeMenu);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !menu.hidden) {
        closeMenu();
        toggle.focus();
      }
    });
  }

  function initIgagyuPage() {
    bindSmoothScroll();
    bindSetAddButtons();
    bindHeaderMenu();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initIgagyuPage);
  } else {
    initIgagyuPage();
  }
})();
